// src/hooks/useClientVolume30d.js
// Volumen de pedidos por comercio en los últimos 30 días, para TODA la cartera
// en UNA llamada: GET reports/dashboard/client-volume (la misma fuente del
// dashboard) devuelve una fila {companyId, total} por cada comercio con
// volumen en el rango. Los comercios sin fila son cero REAL (el endpoint
// enumera todos los que registraron pedidos).
//
// Reemplaza el patrón anterior de 1 request por tarjeta (packages limit:1 →
// totalDocs): mismo número verificado empíricamente (Toolstore 891 = 891),
// pero con costo O(1) y habilitando sumas por subconjunto (KPI que sigue al
// filtro) y ordenamiento por volumen sin requests extra.
import { useCallback, useEffect, useState } from "react";
import useAuth from "./useAuth";
import { dashboardService, getBrowserIanaTimeZone } from "../services/dashboardService";

const CACHE_TTL_MS = 5 * 60 * 1000;

let cached = null; // { promise: Promise<{map,total}|null>, at: number, key: string }

const toIsoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fetchVolume = async (logisticCompanyId) => {
  try {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    // hoy−29 + hoy = 30 días calendario inclusivos (aritmética de calendario,
    // nunca ms: los cambios de hora corren el día — ver useDeliveredWeekly).
    const start = new Date(end);
    start.setDate(end.getDate() - 29);
    const response = await dashboardService.getClientVolume(
      logisticCompanyId,
      toIsoDate(start),
      toIsoDate(end),
      getBrowserIanaTimeZone(),
    );
    const rows = response?.data?.data?.clients ?? response?.data?.clients;
    if (!Array.isArray(rows)) return null;
    const map = new Map();
    let total = 0;
    rows.forEach((r) => {
      if (!r?.companyId || typeof r.total !== "number") return;
      map.set(r.companyId, r.total);
      total += r.total;
    });
    return { map, total };
  } catch {
    return null;
  }
};

// volume: { map: Map<companyId, number>, total: number } | null (error) |
// undefined (cargando). `countOf(companyId)`: número (0 = cero real) o null
// si la carga falló / aún no hay logisticCompanyId.
export function useClientVolume30d() {
  const { user } = useAuth();
  const logisticCompanyId = user?.logisticCompany?._id || user?.logisticCompanyId || null;
  const [volume, setVolume] = useState(undefined);

  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    let alive = true;
    if (
      !cached ||
      cached.key !== logisticCompanyId ||
      Date.now() - cached.at > CACHE_TTL_MS
    ) {
      cached = { promise: fetchVolume(logisticCompanyId), at: Date.now(), key: logisticCompanyId };
    }
    setVolume(undefined);
    cached.promise.then((value) => {
      // Un error no se persiste: el próximo mount reintenta.
      if (value === null) cached = null;
      if (alive) setVolume(value);
    });
    return () => {
      alive = false;
    };
  }, [logisticCompanyId]);

  const loading = volume === undefined;
  // useCallback: countOf entra en deps de useMemo de los consumidores (orden
  // por volumen, sumas por filtro); una identidad nueva por render los rompería.
  const countOf = useCallback(
    (companyId) => {
      if (volume === undefined || volume === null || !companyId) return null;
      return volume.map.get(companyId) ?? 0;
    },
    [volume],
  );

  return { volume, loading, countOf };
}

export default useClientVolume30d;
